import * as THREE from 'three';
import { PLAYER, WORLD } from '../core/Constants.js';
import { EVENTS } from '../core/EventBus.js';

/**
 * PlayerController - dono do MOVIMENTO do jogador (secao 4.9 do ARCHITECTURE.md).
 *
 * Roda em passo fixo de 1/120 s (fixedUpdate), o que deixa a fisica identica em
 * 30, 60 ou 240 fps. O que este modulo faz, em ordem, a cada passo:
 *
 *   entrada -> vetor de desejo no espaco do olhar -> velocidade alvo ->
 *   aceleracao/atrito -> pulo -> gravidade -> ctx.collision.capsuleMove -> eventos
 *
 * O que NAO e responsabilidade daqui: girar a camera (CameraRig), tocar som
 * (AudioSystem escuta FOOTSTEP/PLAYER_LAND/PLAYER_JUMP), balancar a tela
 * (CameraRig le eyeHeight/velocity/lastLandImpact e o evento PLAYER_LAND).
 *
 * Sensacao alvo: pesado mas responsivo. Aceleracao rapida (cerca de 70 ms ate a
 * velocidade de caminhada), parada com atrito alto (nada de patinar no gelo),
 * corrida com rampa de meio segundo, controle no ar reduzido, inercia preservada
 * no salto.
 */

// ---------------------------------------------------------------------------
// Ajustes de SENSACAO. Ficam aqui, e nao em Constants.js, porque sao internos do
// controlador - nenhum outro sistema depende deles. As grandezas fisicas de fato
// compartilhadas (gravidade, velocidades, alturas, coyote, buffer) vem de Constants.
// ---------------------------------------------------------------------------
const SPRINT_RAMP_UP = 1 / 0.55;      // segundos para a corrida atingir a velocidade cheia
const SPRINT_RAMP_DOWN = 1 / 0.16;    // solta o sprint e a velocidade cai rapido
const CROUCH_DOWN_RATE = 1 / 0.17;    // agachar e rapido (reacao a tiro), mas da para ler
const CROUCH_UP_RATE = 1 / 0.24;      // levantar e mais lento, da peso ao corpo
const STRIDE_WALK = 1.85;             // metros por passada andando
const STRIDE_SPRINT = 2.25;           // passada maior correndo, mas cadencia maior no fim
const STRIDE_CROUCH = 1.50;           // agachado: passo curto e espacado no tempo
const STEP_PRIME = 0.62;              // fracao da passada ja "carregada" ao sair do repouso
const MIN_STEP_SPEED = 0.55;          // abaixo disto nao conta passo (encostado em parede)
const BACKPEDAL_SCALE = 0.82;         // andar de re e mais lento
const STRAFE_SCALE = 0.93;            // andar de lado, um pouco mais lento que para frente
const UPHILL_PENALTY = 0.45;          // quanto a rampa mais ingrime tira de velocidade
const GROUND_GLUE = 3.0;              // m/s para baixo enquanto no chao: cola em degrau e descida
const TERMINAL_VELOCITY = 62;         // m/s
const JUMP_LOCK = 0.12;               // refratario do pulo, evita pulo duplo na mesma tecla
const CROUCH_JUMP_SCALE = 0.88;       // pular agachado sobe um pouco menos
const FIRE_SPRINT_LOCK = 0.35;        // atirar cancela a corrida por este tempo
const STAND_CHECK_INTERVAL = 0.08;    // teto baixo: reconferir a cada 80 ms, nao a cada passo
const FALL_SAFE_SPEED = 13.0;         // ate ~3.8 m de queda nao machuca
const FALL_LETHAL_SPEED = 31.0;       // ~22 m de queda mata
const FALL_MAX_DAMAGE = 96;
const LAND_HARD_SPEED = 9.0;          // acima disto a aterrissagem rouba inercia horizontal
const LAND_DAMP = 0.35;               // fracao maxima de velocidade horizontal perdida ao cair
const LAND_MIN_IMPACT = 0.8;          // abaixo disto nem conta como aterrissagem

// Temporarios de modulo. ZERO alocacao por passo - nada de new Vector3() aqui dentro.
const _forward = new THREE.Vector3();
const _right = new THREE.Vector3();
const _wish = new THREE.Vector3();
const _targetVel = new THREE.Vector3();
const _delta = new THREE.Vector3();
const _horiz = new THREE.Vector3();
const _accel = new THREE.Vector3();
const _dirUnit = new THREE.Vector3();
const _perp = new THREE.Vector3();
const _rayOrigin = new THREE.Vector3();
const _prevPos = new THREE.Vector3();
const _up = new THREE.Vector3(0, 1, 0);

const MAX_SLOPE_RAD = (PLAYER.MAX_SLOPE_DEG * Math.PI) / 180;

function clamp(v, min, max) {
  return v < min ? min : (v > max ? max : v);
}

/** Caminha um valor em direcao ao alvo com passo maximo (sem oscilar em volta). */
function approach(current, target, maxStep) {
  const d = target - current;
  if (d > maxStep) return current + maxStep;
  if (d < -maxStep) return current - maxStep;
  return target;
}

/** Curva S: tira o "liga/desliga" da transicao de agachamento. */
function smoothstep(t) {
  return t * t * (3 - 2 * t);
}

export class PlayerController {
  constructor(ctx) {
    this.ctx = ctx;

    // --- estado publico (contrato 4.9) -------------------------------------
    /** Posicao dos PES do jogador (base da capsula). */
    this.position = new THREE.Vector3(0, 0, 0);
    this.velocity = new THREE.Vector3();
    this.grounded = false;
    this.crouching = false;
    this.sprinting = false;
    this.speed = 0;                       // velocidade horizontal em m/s
    this.eyeHeight = PLAYER.EYE_HEIGHT;   // interpolada entre EYE_HEIGHT e CROUCH_EYE_HEIGHT
    this.enabled = true;

    // --- estado publico extra (util para CameraRig / ViewModel / HUD) ------
    this.speedRatio = 0;                  // 0..1 em relacao a SPRINT_SPEED
    this.crouchAmount = 0;                // 0 em pe, 1 agachado (com transicao)
    this.capsuleHeight = PLAYER.HEIGHT;
    this.groundNormal = new THREE.Vector3(0, 1, 0);
    this.surface = 'concrete';            // superficie sob os pes (para passo e som)
    this.airTime = 0;
    this.lastLandImpact = 0;              // m/s da ultima aterrissagem
    this.moveInput = 0;                   // 0..1, intensidade do desejo de andar

    // --- estado interno ----------------------------------------------------
    this._sprintRamp = 0;
    this._coyote = 0;
    this._jumpBuffer = 0;
    this._jumpLock = 0;
    this._jumpWasDown = false;
    this._fireLock = 0;
    this._standCheck = 0;
    this._standBlocked = false;
    this._stepAccum = 0;
    this._wasGrounded = false;
    this._stateFrozen = true;             // comeca no menu; libera em GAME_STATE 'playing'
    this._collisionBroken = false;
    this._rayBroken = false;
    this._warned = new Set();
    this._offEvents = [];
  }

  async init() {
    const ctx = this.ctx;

    // Menu/pausa congelam o mundo. Sem isso o jogador anda por tras do menu.
    this._offEvents.push(ctx.events.on(EVENTS.GAME_STATE, (p) => {
      this._stateFrozen = !p || p.state !== 'playing';
      if (this._stateFrozen) this._jumpWasDown = false;
    }));

    // Atirar cancela a corrida. Vem por evento para nao depender da API interna
    // do WeaponSystem (que pode nem existir ainda quando este modulo sobe).
    this._offEvents.push(ctx.events.on(EVENTS.WEAPON_FIRE, () => {
      this._fireLock = FIRE_SPRINT_LOCK;
      this._sprintRamp = Math.min(this._sprintRamp, 0.25);
    }));

    // Posicao inicial: o GameState reposiciona ao comecar a partida, mas se ele
    // ainda for stub o jogador precisa nascer em lugar valido mesmo assim.
    const spawn = ctx.level?.spawnPoints?.player?.[0];
    if (spawn) {
      if (typeof spawn.x === 'number') this.position.set(spawn.x, spawn.y, spawn.z);
      else if (Array.isArray(spawn) && spawn.length >= 3) this.position.set(spawn[0], spawn[1], spawn[2]);
    }
  }

  // -------------------------------------------------------------------------
  // API publica
  // -------------------------------------------------------------------------

  /** Teleporta os PES do jogador e zera a inercia (respawn, debug, spawn de onda). */
  setPosition(x, y, z) {
    this.position.set(x, y, z);
    this.velocity.set(0, 0, 0);
    this.grounded = false;
    this._wasGrounded = false;
    this.airTime = 0;
    this.speed = 0;
    this.speedRatio = 0;
    this.lastLandImpact = 0;
    this._coyote = 0;
    this._jumpBuffer = 0;
    this._jumpLock = 0;
    this._sprintRamp = 0;
    this._stepAccum = 0;
    this.sprinting = false;
    this.crouching = false;
    this.crouchAmount = 0;
    this.capsuleHeight = PLAYER.HEIGHT;
    this.eyeHeight = PLAYER.EYE_HEIGHT;
    this.groundNormal.set(0, 1, 0);
  }

  /** Escreve a posicao do OLHO em `out` (pes + eyeHeight). Nao aloca. */
  getEyePosition(out) {
    return out.set(this.position.x, this.position.y + this.eyeHeight, this.position.z);
  }

  // -------------------------------------------------------------------------
  // Passo fixo de simulacao
  // -------------------------------------------------------------------------
  fixedUpdate(h) {
    const ctx = this.ctx;
    const input = ctx.input;
    if (!this.enabled || !input) return;

    // Congelado no menu/pausa. O pointer lock serve de valvula de escape: se o
    // menu ainda for stub e nunca emitir 'playing', jogar continua possivel.
    if (this._stateFrozen && !input.locked) {
      this._jumpWasDown = false;
      this.speed = 0;
      this.speedRatio = 0;
      this.sprinting = false;
      return;
    }

    const alive = ctx.player ? ctx.player.alive !== false : true;

    // --- temporizadores ---------------------------------------------------
    if (this._jumpBuffer > 0) this._jumpBuffer -= h;
    if (this._jumpLock > 0) this._jumpLock -= h;
    if (this._fireLock > 0) this._fireLock -= h;
    if (this._standCheck > 0) this._standCheck -= h;

    // --- entrada ----------------------------------------------------------
    let fwd = 0;
    let side = 0;
    let wantCrouch = false;
    let wantSprint = false;

    if (alive) {
      if (input.isDown('forward')) fwd += 1;
      if (input.isDown('back')) fwd -= 1;
      if (input.isDown('right')) side += 1;
      if (input.isDown('left')) side -= 1;
      wantCrouch = input.isDown('crouch');
      wantSprint = input.isDown('sprint');

      // Borda de subida detectada aqui (e nao com wasPressed) porque o passo fixo
      // pode rodar varias vezes - ou nenhuma - dentro do mesmo frame de video.
      const jumpDown = input.isDown('jump');
      if (jumpDown && !this._jumpWasDown) this._jumpBuffer = PLAYER.JUMP_BUFFER;
      this._jumpWasDown = jumpDown;
    } else {
      this._jumpWasDown = false;
    }

    const wishing = (fwd !== 0 || side !== 0);
    this.moveInput = wishing ? 1 : 0;

    // --- agachamento: transicao suave, e so levanta se houver teto ---------
    let crouchTarget = wantCrouch ? 1 : 0;
    if (crouchTarget === 0 && this.crouchAmount > 0.001 && !this._canStand()) {
      crouchTarget = 1; // teto baixo: fica agachado mesmo tendo soltado a tecla
    }
    const crouchRate = crouchTarget > this.crouchAmount ? CROUCH_DOWN_RATE : CROUCH_UP_RATE;
    this.crouchAmount = approach(this.crouchAmount, crouchTarget, crouchRate * h);
    this.crouching = this.crouchAmount > 0.5;
    this.capsuleHeight = PLAYER.HEIGHT + (PLAYER.CROUCH_HEIGHT - PLAYER.HEIGHT) * this.crouchAmount;
    const eyeBlend = smoothstep(this.crouchAmount);
    this.eyeHeight = PLAYER.EYE_HEIGHT + (PLAYER.CROUCH_EYE_HEIGHT - PLAYER.EYE_HEIGHT) * eyeBlend;

    // --- corrida: rampa de velocidade, cancelada ao mirar ou atirar --------
    const ads = ctx.weapons?.isADS === true;
    const sprintBlocked = ads || this._fireLock > 0 || this.crouching || !alive;
    const wantsRun = wantSprint && wishing && fwd > 0 && !sprintBlocked;
    this._sprintRamp = approach(this._sprintRamp, wantsRun ? 1 : 0,
      (wantsRun ? SPRINT_RAMP_UP : SPRINT_RAMP_DOWN) * h);
    this.sprinting = wantsRun && this._sprintRamp > 0.12 && this.speed > 1.2;

    // --- direcao desejada, no espaco do olhar ------------------------------
    const rawYaw = this.ctx.cameraRig?.getYaw?.();
    const yaw = (typeof rawYaw === 'number' && Number.isFinite(rawYaw)) ? rawYaw : 0;
    // yaw = 0 olha para -Z (padrao do three com rotation.order YXZ)
    _forward.set(-Math.sin(yaw), 0, -Math.cos(yaw));
    _right.set(Math.cos(yaw), 0, -Math.sin(yaw));

    _wish.set(0, 0, 0);
    if (wishing) {
      _wish.addScaledVector(_forward, fwd).addScaledVector(_right, side);
      _wish.normalize(); // normalizado: diagonal NAO pode ser mais rapida
    }

    // --- velocidade alvo ---------------------------------------------------
    let target;
    if (ads) target = PLAYER.ADS_SPEED;
    else target = PLAYER.WALK_SPEED + (PLAYER.SPRINT_SPEED - PLAYER.WALK_SPEED) * this._sprintRamp;
    // mistura pelo blend de agachamento para nao ter degrau de velocidade na transicao
    target = target + (PLAYER.CROUCH_SPEED - target) * this.crouchAmount;

    if (wishing) {
      // penalidade por direcao: de re e mais lento, de lado um pouco menos
      const f = clamp(_wish.dot(_forward), -1, 1);
      const dirScale = f >= 0
        ? STRAFE_SCALE + (1 - STRAFE_SCALE) * f
        : STRAFE_SCALE + (BACKPEDAL_SCALE - STRAFE_SCALE) * (-f);
      target *= dirScale;

      // rampa: subir custa velocidade (senao vira escada rolante)
      if (this.grounded) {
        const n = this.groundNormal;
        const ny = clamp(n.y, -1, 1);
        if (ny < 0.999) {
          const horizLen = Math.hypot(n.x, n.z);
          if (horizLen > 1e-4) {
            // a parte horizontal da normal aponta para BAIXO da rampa
            const uphill = clamp(-(_wish.x * n.x + _wish.z * n.z) / horizLen, 0, 1);
            if (uphill > 0) {
              const steep = clamp(Math.acos(ny) / MAX_SLOPE_RAD, 0, 1);
              target *= 1 - UPHILL_PENALTY * steep * uphill;
            }
          }
        }
      }
    }

    // --- aceleracao horizontal --------------------------------------------
    _horiz.set(this.velocity.x, 0, this.velocity.z);

    if (this.grounded) {
      if (wishing) {
        // aceleracao com teto: rapida, mas nunca instantanea, e inverter direcao
        // custa o dobro do tempo (o vetor precisa atravessar o zero)
        _targetVel.copy(_wish).multiplyScalar(target);
        _delta.subVectors(_targetVel, _horiz);
        const len = _delta.length();
        const step = PLAYER.ACCEL_GROUND * h;
        if (len > 1e-5) {
          if (step >= len) _horiz.copy(_targetVel);
          else _horiz.addScaledVector(_delta, step / len);
        }
      } else {
        // atrito exponencial: parada firme em ~0.3 s, estavel em qualquer h
        _horiz.multiplyScalar(Math.exp(-PLAYER.FRICTION * h));
        if (_horiz.lengthSq() < 0.0016) _horiz.set(0, 0, 0); // < 4 cm/s ja e parado
      }
    } else if (wishing) {
      // No ar nao existe atrito: a inercia do salto e preservada. So e possivel
      // acelerar ATE a velocidade alvo na direcao desejada.
      const current = _horiz.x * _wish.x + _horiz.z * _wish.z;
      let add = target - current;
      if (add > 0) {
        let step = PLAYER.ACCEL_AIR * h;
        if (step > add) step = add;
        _accel.copy(_wish).multiplyScalar(step);

        // A parcela que MUDA a direcao do voo e reduzida por AIR_CONTROL; a que
        // segue a direcao do voo passa inteira. Resultado: da para corrigir a
        // trajetoria, mas nao para virar no ar como se estivesse no chao.
        const spd = _horiz.length();
        if (spd > 0.5) {
          _dirUnit.copy(_horiz).multiplyScalar(1 / spd);
          const along = _accel.dot(_dirUnit);
          _perp.copy(_accel).addScaledVector(_dirUnit, -along);
          _accel.copy(_dirUnit).multiplyScalar(along).addScaledVector(_perp, PLAYER.AIR_CONTROL);
        }
        _horiz.add(_accel);
      }
    }

    this.velocity.x = _horiz.x;
    this.velocity.z = _horiz.z;

    // --- pulo: coyote time + jump buffer ----------------------------------
    if (this.grounded) this._coyote = PLAYER.COYOTE_TIME;
    else if (this._coyote > 0) this._coyote -= h;

    let jumped = false;
    if (alive && this._jumpBuffer > 0 && this._jumpLock <= 0 && (this.grounded || this._coyote > 0)) {
      const scale = 1 + (CROUCH_JUMP_SCALE - 1) * this.crouchAmount;
      this.velocity.y = PLAYER.JUMP_VELOCITY * scale;
      this._jumpBuffer = 0;
      this._coyote = 0;
      this._jumpLock = JUMP_LOCK;
      this.grounded = false;
      jumped = true;
      // evento em ritmo humano: clonar a posicao e seguro (o ouvinte pode guardar)
      ctx.events.emit(EVENTS.PLAYER_JUMP, { position: this.position.clone() });
    }

    // --- gravidade ---------------------------------------------------------
    this.velocity.y -= WORLD.GRAVITY * h;
    if (this.velocity.y < -TERMINAL_VELOCITY) this.velocity.y = -TERMINAL_VELOCITY;
    // cola no chao: sem isso o jogador "decola" em cada degrau descido e em rampa
    if (this.grounded && !jumped && this.velocity.y < 0) this.velocity.y = -GROUND_GLUE;

    // --- colisao -----------------------------------------------------------
    const impactVy = this.velocity.y;
    _prevPos.copy(this.position);
    this._resolveMove(h);

    // --- transicoes de chao ------------------------------------------------
    if (this.grounded) {
      this.airTime = 0;
      if (!this._wasGrounded) this._onLand(impactVy);
    } else {
      this.airTime += h;
    }
    this._wasGrounded = this.grounded;

    // --- velocidade e passos ----------------------------------------------
    this.speed = Math.hypot(this.velocity.x, this.velocity.z);
    this.speedRatio = clamp(this.speed / PLAYER.SPRINT_SPEED, 0, 1);

    if (this.grounded) {
      const stride = STRIDE_WALK
        + (STRIDE_SPRINT - STRIDE_WALK) * this._sprintRamp
        + (STRIDE_CROUCH - STRIDE_WALK) * this.crouchAmount;

      // distancia REAL percorrida: empurrar uma parede nao gera passo
      const dx = this.position.x - _prevPos.x;
      const dz = this.position.z - _prevPos.z;
      const moved = Math.hypot(dx, dz);

      if (moved / h > MIN_STEP_SPEED) {
        this._stepAccum += moved;
        if (this._stepAccum >= stride) {
          this._stepAccum -= stride;
          if (this._stepAccum > stride) this._stepAccum = 0;
          this._emitFootstep();
        }
      } else if (this._stepAccum < stride * STEP_PRIME) {
        // parado: deixa a proxima passada quase pronta, para o primeiro passo
        // sair junto com o inicio do movimento
        this._stepAccum = stride * STEP_PRIME;
      }
    }
  }

  // -------------------------------------------------------------------------
  // Internos
  // -------------------------------------------------------------------------

  /** Move a capsula pelo mundo. Degrada para integracao simples se a colisao falhar. */
  _resolveMove(h) {
    const col = this.ctx.collision;
    let res = null;

    if (!this._collisionBroken && col && typeof col.capsuleMove === 'function') {
      try {
        res = col.capsuleMove(this.position, this.velocity, h, PLAYER.RADIUS, this.capsuleHeight);
      } catch (err) {
        this._collisionBroken = true;
        this._warnOnce('capsuleMove falhou; movimento sem colisao', err);
        res = null;
      }
    }

    // Exige uma posicao de verdade na resposta: colisao pela metade (stub que
    // devolve objeto vazio) travaria o jogador no lugar sem nenhum aviso.
    if (res && res.position && typeof res.position.x === 'number') {
      this.position.copy(res.position);
      if (res.velocity && typeof res.velocity.x === 'number') this.velocity.copy(res.velocity);
      this.grounded = res.grounded === true;
      if (this.grounded && res.normal && typeof res.normal.y === 'number') this.groundNormal.copy(res.normal);
      else this.groundNormal.set(0, 1, 0);
    } else {
      // Fallback: integra livre e usa um piso em y = 0. Serve so para o jogo
      // continuar de pe enquanto CollisionWorld ainda for stub.
      this.position.x += this.velocity.x * h;
      this.position.y += this.velocity.y * h;
      this.position.z += this.velocity.z * h;
      if (this.position.y <= 0) {
        this.position.y = 0;
        if (this.velocity.y < 0) this.velocity.y = 0;
        this.grounded = true;
      } else {
        this.grounded = false;
      }
      this.groundNormal.set(0, 1, 0);
    }

    // Rede de seguranca: um NaN vindo da colisao envenena camera, audio e IA e
    // nunca mais sai. Melhor perder um passo do que perder a partida.
    if (!Number.isFinite(this.position.x) || !Number.isFinite(this.position.y) || !Number.isFinite(this.position.z)) {
      this.position.copy(_prevPos);
      this.velocity.set(0, 0, 0);
      this.grounded = true;
      this._warnOnce('posicao invalida (NaN) devolvida pela colisao');
    }
    if (!Number.isFinite(this.velocity.x) || !Number.isFinite(this.velocity.y) || !Number.isFinite(this.velocity.z)) {
      this.velocity.set(0, 0, 0);
    }
  }

  /** Aterrissagem: som, camera, perda de inercia e dano de queda. */
  _onLand(impactVy) {
    const impact = impactVy < 0 ? -impactVy : 0;
    this.lastLandImpact = impact;
    this._sampleSurface();

    // Queda forte rouba parte da inercia horizontal - o corpo "absorve" o baque.
    if (impact > LAND_HARD_SPEED) {
      const k = clamp((impact - LAND_HARD_SPEED) / 14, 0, 1);
      const damp = 1 - LAND_DAMP * k;
      this.velocity.x *= damp;
      this.velocity.z *= damp;
    }

    // Toque de 5 cm (spawn, quina de calcada, ressalto do "cola no chao") nao e
    // aterrissagem: evita som e tranco de camera fantasma.
    if (impact > LAND_MIN_IMPACT) {
      this.ctx.events.emit(EVENTS.PLAYER_LAND, {
        position: this.position.clone(),
        surface: this.surface,
        impact
      });
    }

    // Dano de queda: nada ate ~3.8 m, doi de verdade acima de ~10 m, mata acima
    // de ~22 m. Curva quadratica para a queda media so assustar.
    if (impact > FALL_SAFE_SPEED) {
      const t = clamp((impact - FALL_SAFE_SPEED) / (FALL_LETHAL_SPEED - FALL_SAFE_SPEED), 0, 1);
      const dmg = Math.round(t * t * FALL_MAX_DAMAGE + t * 8);
      if (dmg > 0) this.ctx.player?.damage?.(dmg, null, 'leg');
    }

    // Passo imediato ao tocar o chao teria som duplo com PLAYER_LAND: zera.
    this._stepAccum = 0;
  }

  _emitFootstep() {
    this._sampleSurface();
    this.ctx.events.emit(EVENTS.FOOTSTEP, {
      position: this.position.clone(),
      surface: this.surface,
      isPlayer: true,
      running: this.sprinting
    });
  }

  /**
   * Le a superficie sob os pes. Chamado so em passo/aterrissagem (poucas vezes
   * por segundo), nunca a 120 Hz.
   * O maxY em position.y + 0.6 serve as duas leituras possiveis da API: "altura
   * de onde comeca o raio" e "distancia maxima de busca".
   */
  _sampleSurface() {
    const col = this.ctx.collision;
    if (!col || typeof col.sampleGround !== 'function') return;
    try {
      const g = col.sampleGround(this.position.x, this.position.z, this.position.y + 0.6);
      if (g && typeof g.surface === 'string') this.surface = g.surface;
    } catch (err) {
      this._warnOnce('sampleGround falhou', err);
    }
  }

  /**
   * Ha espaco para ficar de pe? Cinco raios (centro + 4 nas bordas da capsula)
   * porque um raio central sozinho deixa levantar dentro da quina de uma laje.
   * Resultado em cache por STAND_CHECK_INTERVAL: so e chamado quando o jogador
   * esta tentando levantar, mas mesmo assim nao precisa de 120 leituras por segundo.
   */
  _canStand() {
    const col = this.ctx.collision;
    if (this._rayBroken || !col || typeof col.raycast !== 'function') return true;
    if (this._standCheck > 0) return !this._standBlocked;
    this._standCheck = STAND_CHECK_INTERVAL;

    const base = 0.12;
    const dist = PLAYER.HEIGHT + 0.06 - base;
    const r = PLAYER.RADIUS * 0.75;
    let blocked = false;

    for (let i = 0; i < 5 && !blocked; i++) {
      const ox = i === 1 ? r : (i === 2 ? -r : 0);
      const oz = i === 3 ? r : (i === 4 ? -r : 0);
      _rayOrigin.set(this.position.x + ox, this.position.y + base, this.position.z + oz);
      try {
        if (col.raycast(_rayOrigin, _up, dist)) blocked = true;
      } catch (err) {
        // So o raio de teto desiste; o movimento com capsula continua tentando.
        this._rayBroken = true;
        this._warnOnce('raycast falhou na checagem de teto', err);
        return true;
      }
    }

    this._standBlocked = blocked;
    return !blocked;
  }

  /** Um aviso por problema, uma vez so. Sistema quebrado degrada, nao derruba o frame. */
  _warnOnce(msg, err) {
    if (this._warned.has(msg)) return;
    this._warned.add(msg);
    console.warn(`[PlayerController] ${msg}`, err || '');
  }

  dispose() {
    for (const off of this._offEvents) {
      try { off(); } catch { /* ouvinte ja removido */ }
    }
    this._offEvents.length = 0;
  }
}
